import React, { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import ButtonSlim from '../../../../web-app-components/components/ButtonSlim';
import TextInput from '../../../../web-app-components/components/TextInput';
import useStateThrottled from '../../../../web-app-components/hooks/useStateThrottled';
import makeStyles from '@material-ui/core/styles/makeStyles';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import SearchIcon from '@material-ui/icons/Search';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { DataGrid } from '@material-ui/data-grid';

import routesClient from '../../../../clients/routes';
import { REGIONS } from '../../../Objects/components/Form/components/RegionSelectField';

const getRegionLabel = (value) => {
  if (value == null || value === '') return '';
  const region = REGIONS.find((r) => r.value === value);
  return region ? region.label : value;
};

const columns = [
  {
    field: 'Title',
    headerName: 'Pavadinimas',
    flex: 1,
    sortable: false,
  },
  {
    field: 'Region',
    headerName: 'Regionas',
    flex: 0.5,
    sortable: false,
    valueGetter: (params) => getRegionLabel(params.row?.Region),
  },
  {
    field: 'District',
    headerName: 'Savivaldybė',
    flex: 0.5,
    sortable: false,
  },
  {
    field: 'Paid',
    headerName: 'Mokamas',
    flex: 0.35,
    sortable: false,
    valueGetter: (params) => (params.row?.Paid ? 'Taip' : 'Ne'),
  },
  {
    field: 'PaidAmount',
    headerName: 'Kaina (€)',
    flex: 0.4,
    sortable: false,
    valueGetter: (params) => {
      const paid = params.row?.Paid;
      const amount = params.row?.PaidAmount;
      if (!paid || amount == null || amount === '') return '—';
      return `${Number(amount)} €`;
    },
  },
];

const useStyles = makeStyles((theme) => ({
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1),
    flexWrap: 'wrap',

    '& > :not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },
  searchInput: {
    flex: '0 1 300px',
  },
  rightControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
}));

const RoutesList = () => {
  const classes = useStyles();

  const history = useHistory();
  const { path } = useRouteMatch();

  const { enqueueSnackbar } = useSnackbar();

  const [throttledSearchTerm, searchTerm, setSearchTerm] = useStateThrottled('', 500);
  const [paidOnly, setPaidOnly] = useState(false);
  const [routes, setRoutes] = useState();

  // Fetch routes with filters
  useEffect(() => {
    (async () => {
      try {
        const query = { 
          sort: 'createdAt:desc'
        };
        
        const filtersObj = {};
        
        if (throttledSearchTerm && throttledSearchTerm.trim()) {
          filtersObj.Title = { $containsi: throttledSearchTerm.trim() };
        }
        
        if (paidOnly) {
          filtersObj.Paid = { $eq: true };
        }
        
        if (Object.keys(filtersObj).length > 0) {
          query.filters = filtersObj;
        }
        
        setRoutes(await routesClient.find(query));
      } catch (error) {
        enqueueSnackbar(
          'Nepavyko gauti maršrutų sąrašo. Bandykite atidaryti iš naujo.',
          { variant: 'error' }
        );
      }
    })();
  }, [throttledSearchTerm, paidOnly, enqueueSnackbar]);

  const handleSearchChange = ({ target: { value } }) => setSearchTerm(value);

  const handleNewRouteClick = () => history.push(`${path}/new`);

  const handleRowClick = ({ id }) => history.push(`${path}/${id}`);

  return (
    <div>
      <div className={classes.controls}>
        <TextInput
          className={classes.searchInput}
          placeholder="Ieškoti pagal pavadinimą..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <div className={classes.rightControls}>
          <FormControlLabel
            control={
              <Checkbox
                checked={paidOnly}
                onChange={(e) => setPaidOnly(e.target.checked)}
                color="primary"
              />
            }
            label="Tik mokami maršrutai"
          />
          <ButtonSlim startIcon={<AddIcon />} onClick={handleNewRouteClick}>
            Naujas maršrutas
          </ButtonSlim>
        </div>
      </div>
      <Paper>
        <DataGrid
          columns={columns}
          rows={routes || []}
          loading={!routes}
          onRowClick={handleRowClick}
          autoHeight
          disableColumnMenu
          disableSelectionOnClick
        />
      </Paper>
    </div>
  );
};

export default RoutesList;
